'use client';

import { useState } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  Dialog,
  DialogActionClose,
  DialogActionPrimary,
  DialogActions,
  DialogContent,
  DialogTrigger,
} from '@sola/ark-core/molecules/dialog/dialog.tsx';
import { EmailField } from '@sola/ark-core/molecules/email-field/email-field.tsx';
import { Form } from '@sola/ark-core/molecules/form/form.tsx';
import { InputField } from '@sola/ark-core/molecules/input-field/input-field.tsx';
import { PhoneField } from '@sola/ark-core/molecules/phone-field/phone-field.tsx';
import { useContactInfoUpdate } from '@sola/my-sdk/settings/hooks.ts';
import { COUNTRY_CODE_US } from '@sola/schema/dotcom/shared/geo.ts';
import type { GraphQLSupporterContact } from '@sola/schema/dotcom/supporters/schema.generated.ts';

import {
  LocalAlertsProvider,
  useLocalAlerts,
} from '../../molecules/local-alerts/local-alerts-provider.tsx';
import { LocalAlerts } from '../../molecules/local-alerts/local-alerts.tsx';
import {
  createContactInfoUpdateErrorAlert,
  createContactInfoUpdateSuccessAlert,
} from '../../utils/alerts.tsx';

type ContactFormValues = {
  firstname: string;
  lastname: string;
  preferredName: string;
  email: string;
  phone: string;
};

const ContactModalForm = ({
  defaultFormValues,
  contactId,
  onSuccess,
}: {
  defaultFormValues: ContactFormValues;
  contactId: string;
  onSuccess: () => void;
}) => {
  const { addAlerts, clearAlerts } = useLocalAlerts();
  const { mutateAsync: updateContactInfo } = useContactInfoUpdate();
  const [isBusy, setIsBusy] = useState(false);
  const { t } = useTranslation(['core', 'my-settings']);

  const handleSubmit = async (data: FieldValues) => {
    setIsBusy(true);
    clearAlerts();
    try {
      await updateContactInfo({
        contactId: contactId,
        contactInfo: {
          firstName: data.firstname as string,
          lastName: data.lastname as string,
          preferredName: data.preferredName as string | undefined,
          email: data.email as string,
          phone: data.phone as string | undefined,
        },
      });
      onSuccess();
    } catch {
      addAlerts(createContactInfoUpdateErrorAlert());
      setIsBusy(false);
    }
  };

  return (
    <Form
      onSubmit={handleSubmit}
      options={{
        defaultValues: defaultFormValues,
        mode: 'onChange',
      }}
    >
      <InputField
        key="firstname"
        name="firstname"
        label={t('my-settings:form.contactInformation.firstNameLabel')}
        required
      />
      <InputField
        key="lastname"
        name="lastname"
        label={t('my-settings:form.contactInformation.lastNameLabel')}
        required
      />
      <InputField
        key="preferredName"
        name="preferredName"
        label={t('my-settings:form.contactInformation.preferredName')}
        hint={t('my-settings:form.contactInformation.preferredNameHelper')}
      />
      <EmailField
        key="email"
        name="email"
        required
        label={t('my-settings:form.contactInformation.emailAddress')}
      />
      <PhoneField
        key="phone"
        name="phone"
        label={t('my-settings:form.contactInformation.phoneNumberLabel')}
        countryCode={COUNTRY_CODE_US}
      />
      <DialogActions>
        <DialogActionClose>{t('core:actions.cancel')}</DialogActionClose>
        <DialogActionPrimary type="submit" disableUntilFilled isBusy={isBusy}>
          {t('core:actions.save')}
        </DialogActionPrimary>
      </DialogActions>
    </Form>
  );
};

export const SettingsModalContact = ({
  contact,
  open,
  openTrigger,
  onOpenChange,
}: {
  contact: GraphQLSupporterContact;
  open?: boolean;
  openTrigger?: React.ReactElement<HTMLButtonElement>;
  onOpenChange?: (open: boolean) => void;
}) => {
  const sectionAlerts = useLocalAlerts();
  const [isOpenInternal, setIsOpenInternal] = useState(false);
  const isOpen = open ?? isOpenInternal;

  const defaultFormValues = {
    firstname: contact.firstName ?? '',
    lastname: contact.lastName ?? '',
    preferredName: contact.preferredName ?? '',
    email: contact.email ?? '',
    phone: contact.phone ?? '',
  };

  const { t } = useTranslation(['core', 'my-settings']);

  const handleSuccess = () => {
    sectionAlerts.addAlerts(createContactInfoUpdateSuccessAlert());
    setIsOpenInternal(false);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setIsOpenInternal(nextOpen);
    onOpenChange?.(nextOpen);
    sectionAlerts.clearAlerts();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      {openTrigger && <DialogTrigger>{openTrigger}</DialogTrigger>}

      <DialogContent
        icon="user-edit"
        title={t('my-settings:form.contactInformation.title')}
      >
        <LocalAlertsProvider>
          <LocalAlerts />
          <ContactModalForm
            defaultFormValues={defaultFormValues}
            contactId={contact.id}
            onSuccess={handleSuccess}
          />
        </LocalAlertsProvider>
      </DialogContent>
    </Dialog>
  );
};
